import React from 'react';
import { Box, Center, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { IoCheckmark } from 'react-icons/io5';

import { useSiteDeviceIcon } from '../hooks/use-site-device-icon';

export type DeviceOption = {
  siteDeviceId: number;
  isSelected: boolean;
  label?: string;
};

export type SiteDeviceOptionSelectProps = {
  deviceOptions: DeviceOption[];
  onToggleDeviceSelection: (siteDeviceId: number) => void;
};

/**
 * Displays a set of selectable device options for a site, with icons respective to the supported device type.
 * Raises selection state management to consumers.
 *
 * @param deviceOptions - List of device options to display.
 * @param onToggleDeviceSelection - Handler for toggling selection of a device option.
 */
export function SiteDeviceOptionSelect({ deviceOptions, onToggleDeviceSelection }: SiteDeviceOptionSelectProps) {
  const background = useColorModeValue('white', 'dusk100.800');

  if (!deviceOptions.length) return null;

  return (
    <Box>
      <HStack columnGap={6} bg={background} p={4} minH={'96px'} data-testid="device-option-select-list">
        {deviceOptions.map((device, index) => (
          <SiteDeviceOption
            onToggleDeviceSelection={onToggleDeviceSelection}
            device={device}
            key={device.siteDeviceId}
            deviceIndex={index}
          />
        ))}
      </HStack>
    </Box>
  );
}

type SiteDeviceOptionProps = {
  device: DeviceOption;
  deviceIndex: number;
  onToggleDeviceSelection: (siteDeviceId: number) => void;
};

const SiteDeviceOption = ({ device, deviceIndex, onToggleDeviceSelection }: SiteDeviceOptionProps) => {
  const background = useColorModeValue('white', 'gray.700');
  const Icon = useSiteDeviceIcon(device.siteDeviceId);

  return (
    <Box>
      <Center
        w={16}
        h={16}
        border={'1px solid'}
        borderRadius={'50%'}
        position={'relative'}
        onClick={() => onToggleDeviceSelection(device.siteDeviceId)}
        data-testid={`device-option-${deviceIndex}`}
        data-is-selected={device.isSelected ? 'true' : undefined}
      >
        <Center
          position={'absolute'}
          top={-1}
          left={-1}
          w={6}
          h={6}
          border={'1px solid'}
          borderColor={'gray.400'}
          borderRadius={4}
          bg={background}
        >
          {device.isSelected && (
            <Icon
              as={IoCheckmark}
              ml="auto"
              color="green.500"
              w={6}
              h={6}
              data-testid="device-option-selected-checkmark"
            />
          )}
        </Center>
        <Icon w={6} h={6} />
      </Center>
      {device?.label && <Text mt={3}>{device.label}</Text>}
    </Box>
  );
};
